import React from 'react';
import styled from '@emotion/styled';
import { useLang } from '../../context/LanguageContext';
import { t } from '../../translations';
import ClaudeCodeLogo from '../../assets/svg/header/claude-code-logo.svg';
import ReactLogo from '../../assets/svg/tech/react.svg';
import ReactNativeLogo from '../../assets/svg/tech/react-native.svg';
import ExpoLogo from '../../assets/svg/tech/expo.svg';
import TypeScriptLogo from '../../assets/svg/tech/typescript.svg';
import NodeLogo from '../../assets/svg/tech/nodejs.svg';
import PostgreSQLLogo from '../../assets/svg/tech/postgresql.svg';
import NextJsLogo from '../../assets/svg/tech/nextjs.svg';
import DockerLogo from '../../assets/svg/tech/docker.svg';
import AWSLogo from '../../assets/svg/tech/aws.svg';
import FirebaseLogo from '../../assets/svg/tech/firebase.svg';
import JavaLogo from '../../assets/svg/tech/java.svg';
import KotlinLogo from '../../assets/svg/tech/kotlin.svg';
import SpringBootLogo from '../../assets/svg/tech/spring-boot.svg';
import MongoDBLogo from '../../assets/svg/tech/mongodb.svg';
import OracleLogo from '../../assets/svg/tech/oracle.svg';
import KafkaLogo from '../../assets/svg/tech/kafka.svg';

const Section = styled.section`
  background: #F5F5F5;
  border-top: 1px solid #E8E8E8;
  border-bottom: 1px solid #E8E8E8;
  padding: 100px 50px;

  @media (max-width: 640px) {
    padding: 64px 20px;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  margin-bottom: 72px;
  text-align: center;

  @media (max-width: 640px) {
    text-align: left;
  }
`;

const Label = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #D97757;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0 0 16px;
`;

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  color: #0A0A0A;
  letter-spacing: -1px;
  margin: 0 0 16px;
`;

const Subtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: #666;
  line-height: 1.7;
  max-width: 480px;
  margin: 0 auto;

  @media (max-width: 640px) {
    margin: 0;
  }
`;

const FeaturedCard = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  background: #FFFFFF;
  border: 1px solid rgba(217, 119, 87, 0.3);
  border-radius: 16px;
  padding: 40px 48px;
  margin-bottom: 48px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(217,119,87,0.5), transparent);
  }

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const FeaturedLogoWrap = styled.div`
  flex-shrink: 0;
  background: rgba(217, 119, 87, 0.06);
  border: 1px solid rgba(217, 119, 87, 0.15);
  border-radius: 12px;
  padding: 20px 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeaturedContent = styled.div``;

const FeaturedBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  background: rgba(217,119,87,0.12);
  border: 1px solid rgba(217,119,87,0.3);
  border-radius: 100px;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #D97757;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const FeaturedTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #0A0A0A;
  margin: 0 0 10px;
`;

const FeaturedDesc = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  color: #777;
  line-height: 1.65;
  margin: 0;
  max-width: 540px;
`;

const Categories = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const CategoryBlock = styled.div``;

const CategoryLabel = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #AAAAAA;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #E8E8E8;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  @media (max-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TechCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E8E8E8;
  border-radius: 12px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  aspect-ratio: 1;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: border-color 200ms ease, box-shadow 200ms ease;

  &:hover {
    border-color: #D0D0D0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }
`;

const TechLogo = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;

const TechName = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
`;

const techs = [
  { logo: ReactLogo, name: 'React' },
  { logo: ReactNativeLogo, name: 'React Native' },
  { logo: ExpoLogo, name: 'Expo' },
  { logo: TypeScriptLogo, name: 'TypeScript' },
  { logo: NextJsLogo, name: 'Next.js' },
  { logo: NodeLogo, name: 'Node.js' },
  { logo: JavaLogo, name: 'Java' },
  { logo: SpringBootLogo, name: 'Spring Boot' },
  { logo: KotlinLogo, name: 'Kotlin' },
  { logo: PostgreSQLLogo, name: 'PostgreSQL' },
  { logo: MongoDBLogo, name: 'MongoDB' },
  { logo: OracleLogo, name: 'Oracle' },
  { logo: KafkaLogo, name: 'Apache Kafka' },
  { logo: DockerLogo, name: 'Docker' },
  { logo: AWSLogo, name: 'AWS' },
  { logo: FirebaseLogo, name: 'Firebase' },
];

const Technologies = () => {
  const { lang } = useLang();
  const tr = t[lang].technologies;

  return (
    <Section>
      <Container>
        <SectionHeader>
          <Label>{tr.label}</Label>
          <Title>{tr.title}</Title>
          <Subtitle>{tr.subtitle}</Subtitle>
        </SectionHeader>

        <Grid>
          {techs.map(tech => (
            <TechCard key={tech.name}>
              <TechLogo src={tech.logo} alt={tech.name} />
              <TechName>{tech.name}</TechName>
            </TechCard>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default Technologies;
